import React, { useCallback, useEffect, useState } from "react";
import { Brain } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import api from "../../services/api";

const MarketSentiment = ({ symbol }) => {
  const [sentiment, setSentiment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchSentiment = useCallback(async () => {
    if (!symbol) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/news-events/sentiment?symbol=${symbol}`);
      setSentiment({
        ...(res.data?.sentiment || {}),
        eventsAnalyzed: Number(res.data?.events_analyzed || 0),
      });
    } catch (err) {
      console.error("Failed to fetch sentiment:", err);
      setError("Could not load sentiment right now.");
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  useEffect(() => {
    setSentiment(null);
    setError("");
  }, [symbol]);

  return (
    <Card className="bg-white/5 border-white/10 mb-4">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-xs font-medium text-zinc-400 flex items-center gap-2">
          <Brain className="w-3.5 h-3.5 text-purple-400" />
          Market Sentiment ({symbol})
        </CardTitle>
        <div className="flex items-center gap-2">
          {sentiment && (
            <Badge variant="outline" className={`text-[10px] uppercase ${
              sentiment.label === "bullish" ? "text-green-400 border-green-400/20" :
              sentiment.label === "bearish" ? "text-red-400 border-red-400/20" :
              "text-zinc-400 border-zinc-400/20"
            }`}>
              {sentiment.label}
            </Badge>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[11px]"
            onClick={fetchSentiment}
            disabled={loading || !symbol}
          >
            {loading ? "Loading..." : "Get Sentiment"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-12 flex items-center justify-center">
            <div className="animate-pulse text-[11px] text-zinc-400 italic">AI analyzing headlines...</div>
          </div>
        ) : error ? (
          <div className="text-[11px] text-red-300">{error}</div>
        ) : !sentiment ? (
          <div className="text-[11px] text-zinc-400">
            Click <span className="text-zinc-300">Get Sentiment</span> to run analysis on demand.
          </div>
        ) : sentiment?.eventsAnalyzed === 0 ? (
          <div className="space-y-1 text-[11px] text-zinc-400">
            <div>No upcoming event headlines are configured for {symbol} yet.</div>
            <div className="text-zinc-400">Add future-dated events in Admin to power news sentiment.</div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="text-2xl font-bold font-mono tracking-tight">
                {sentiment?.score > 0 ? "+" : ""}{sentiment?.score?.toFixed(2) || "0.00"}
              </div>
              <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-1000 ${
                    sentiment?.score >= 0 ? "bg-green-500" : "bg-red-500"
                  }`}
                  style={{ 
                    width: `${Math.abs(sentiment?.score || 0) * 100}%`,
                    marginLeft: sentiment?.score >= 0 ? "0" : "auto"
                  }}
                />
              </div>
            </div>
            {sentiment?.rationale && (
              <p className="text-[11px] text-zinc-400 leading-relaxed italic">
                "{sentiment.rationale}"
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default React.memo(MarketSentiment);
